import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { emailConfig } from '../emailConfig';
import './Contact.css';

function Contact() {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Pre-fill form if coming from property listing
  useEffect(() => {
    if (location.state?.propertyAddress) {
      const { propertyAddress, propertyPrice } = location.state;
      setFormData(prev => ({
        ...prev,
        subject: `Inquiry about ${propertyAddress}`,
        message: `I am interested in learning more about the property at ${propertyAddress} listed at ${propertyPrice}.\n\n`
      }));
    }
  }, [location.state]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Initialize EmailJS
      emailjs.init(emailConfig.publicKey);

      // Send to first recipient (Brad)
      const templateParams1 = {
        to_email: 'bradkashani@gmail.com',
        title: formData.subject || 'Website Contact Form',
        name: formData.name,
        email: formData.email,
        time: new Date().toLocaleString(),
        message: formData.message,
        reply_to: formData.email
      };

      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.contactTemplateId,
        templateParams1
      );

      // Send to second recipient (Gita)
      const templateParams2 = {
        to_email: 'gita@reaestatesourceinc.com',
        title: formData.subject || 'Website Contact Form',
        name: formData.name,
        email: formData.email,
        time: new Date().toLocaleString(),
        message: formData.message,
        reply_to: formData.email
      };

      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.contactTemplateId,
        templateParams2
      );

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });

      // Navigate back to home page after successful submission (only after user sees success message)
      setTimeout(() => {
        navigate('/');
      }, 3000);

    } catch (error) {
      console.error('Detailed error submitting form:', error);
      console.error('Error message:', error.message);
      console.error('Error status:', error.status);
      console.error('Error text:', error.text);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearForm = () => {
    setFormData({ name: '', email: '', subject: '', message: '' });
    setSubmitStatus(null);
  };

  const [showForm, setShowForm] = useState(true);

  const handleCloseForm = () => {
    navigate('/');
  };

  return (
    <div className="contact">
      <div className="container">
        <h1>Contact Us</h1>
        <p className="contact-intro">
          We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </p>
        
        <div className="contact-content">
          <div className="contact-form-section">
            {showForm ? (
              <form onSubmit={handleSubmit} className="contact-form">
                <button 
                  type="button" 
                  className="close-form-button"
                  onClick={handleCloseForm}
                >
                  ×
                </button>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  required
                ></textarea>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                <button 
                  type="button" 
                  className="clear-button"
                  onClick={handleClearForm}
                  disabled={isSubmitting}
                >
                  Clear Form
                </button>
              </div>
              
              {submitStatus === 'success' && (
                <div className="status-message success">
                  Message sent successfully to Gita! You'll be redirected to the home page shortly.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="status-message error">
                  There was an error sending your message. Please call (650) 283-3537 or email bradkashani@gmail.com directly.
                </div>
              )}
            </form>
            ) : (
              <div className="form-closed-message">
                <h3>Contact form closed</h3>
                <p>Please use the contact information on the right to reach out directly.</p>
                <button 
                  className="reopen-form-button"
                  onClick={() => setShowForm(true)}
                >
                  Reopen Form
                </button>
              </div>
            )}
          </div>
          
          <div className="contact-info">
            <div className="info-section">
              <h3>Get in Touch</h3>
              <div className="info-item">
                <strong>Email:</strong>
                <p>gita@realestatesourceinc.com</p>
              </div>
              <div className="info-item">
                <strong>Phone:</strong>
                <p>
                  <span className="phone-desktop">(650) 283-3537</span>
                  <a href="tel:650-283-3537" className="phone-mobile contact-btn primary">Call (650) 283-3537</a>
                </p>
              </div>
              <div className="info-item">
                <strong>Address:</strong>
                <p>Los Altos, CA</p>
              </div>
            </div>
            
            <div className="info-section">
              <h3>Business Hours</h3>
              <div className="hours">
                <p><strong>Monday - Sunday:</strong> 9:00 AM - 6:00 PM</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;